import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import StayCalendar from "./StayCalendar";
import { ARRIVAL, DEPARTURE, resolveDayClick } from "../../helper/stayRange";

const toDate = (value) => (value ? new Date(value) : null);
const formatDate = (date) => moment(date).format("DD.MM.YYYY");

// Two months fit once the section is this wide.
const TWO_MONTHS_MIN_WIDTH = 540;

const useMonthsShown = (ref) => {
  const [months, setMonths] = useState(1);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () =>
      setMonths(element.clientWidth >= TWO_MONTHS_MIN_WIDTH ? 2 : 1);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);

  return months;
};

// The stay calendar right on the page of an apartment, instead of a room choice.
const StayCalendarSection = ({
  arrivalDate,
  departureDate,
  bookedDates,
  minimumStayMap,
  getDayClassName,
  renderDayContents,
  onChange,
  location,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const monthsShown = useMonthsShown(ref);

  const arrival = toDate(arrivalDate);
  const departure = toDate(departureDate);
  // After the arrival the next click picks the departure; a complete stay starts over.
  const activeField = arrival && !departure ? DEPARTURE : ARRIVAL;

  const handleDayClick = (date) => {
    if (!date) return;
    const result = resolveDayClick({
      activeField,
      date,
      arrivalDate: arrival,
      departureDate: activeField === ARRIVAL ? null : departure,
      bookedDates,
      minimumStayMap,
    });
    onChange({ arrivalDate: result.arrivalDate, departureDate: result.departureDate });
  };

  const subtitle =
    arrival && departure
      ? `${formatDate(arrival)} - ${formatDate(departure)}`
      : arrival
      ? t("select_departure_date", "Select your departure date")
      : t("select_arrival_date", "Select your arrival date");

  return (
    <section className='stay-calendar-section' ref={ref}>
      <h3 className='stay-calendar-section__title'>
        {location
          ? t("stay_in_location", { location, defaultValue: "Stay in {{location}}" })
          : t("availability")}
      </h3>
      <p className='stay-calendar-section__dates'>{subtitle}</p>
      <StayCalendar
        activeField={activeField}
        arrival={arrival}
        departure={departure}
        bookedDates={bookedDates}
        minimumStayMap={minimumStayMap}
        getDayClassName={getDayClassName}
        renderDayContents={renderDayContents}
        onDayClick={handleDayClick}
        onClear={() => onChange({ arrivalDate: null, departureDate: null })}
        monthsShown={monthsShown}
      />
    </section>
  );
};

export default StayCalendarSection;
